import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { StatusMessage } from 'components/molecules';
import ApiService from 'services/api';
import AuthService from 'services/auth';
import './CallbackPage.css';

/**
 * Callback Page
 * Handles the OAuth callback from Google and Microsoft services
 */
const CallbackPage = ({ provider = 'google' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState({
    loading: true,
    success: false,
    error: null
  });
  
  // Check if this is a popup window (OAuth flow)
  const isPopup = window.opener && window.opener !== window;

  // Provider-specific configurations
  const getProviderConfig = (provider) => {
    const configs = {
      google: {
        name: 'Google',
        serviceName: 'Google Calendar & Gmail',
        color: 'primary',
        handler: ApiService.handleGoogleCallback
      },
      microsoft: {
        name: 'Microsoft',
        serviceName: 'Outlook & Microsoft Calendar',
        color: 'info',
        handler: ApiService.handleMicrosoftCallback
      },
      facebook: {
        name: 'Facebook',
        serviceName: 'Facebook & Instagram',
        color: 'primary',
        handler: ApiService.handleFacebookCallback
      }
    };
    return configs[provider] || configs.google;
  };

  const providerConfig = getProviderConfig(provider);

  useEffect(() => {
    handleCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCallback = async () => {
    console.log('🔄 UPDATED CALLBACK PAGE - Processing OAuth callback...');
    console.log('📍 Current URL:', window.location.href);
    console.log('🔍 URL params:', Object.fromEntries(searchParams.entries()));
    
    // Check for direct OAuth callback parameters (new account-based flow)
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const accountId = searchParams.get('account_id');
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const sessionToken = searchParams.get('sessionToken');

    // Handle OAuth error
    if (error || success === 'false') {
      setStatus({
        loading: false,
        success: false,
        error: error || 'OAuth authentication failed. Please try again.'
      });
      return;
    }

    // Handle OAuth success (new account-based flow)
    if (success === 'true' && accountId && sessionToken) {
      try {
        // Create user session with account-based data
        const user = AuthService.createUserSession({
          account_id: accountId,
          email: email,
          name: name,
          provider: provider,
          sessionToken: sessionToken
        });

        console.log(`✅ ${providerConfig.name} OAuth successful:`, user);

        setStatus({
          loading: false,
          success: true,
          error: null
        });

        if (isPopup) {
          // Send success result to parent window and close popup
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_SUCCESS',
              provider,
              account_id: accountId,
              email: email,
              name: name,
              sessionToken: sessionToken
            }, '*');
            window.close();
          }
        } else {
          // Check if this is a CLI authorization flow
          const deviceCode = sessionStorage.getItem('cli_device_code');
          
          if (deviceCode) {
            // CLI login flow - redirect to authorization page
            sessionStorage.removeItem('cli_device_code');
            setTimeout(() => {
              navigate(`/authorize?deviceCode=${deviceCode}`);
            }, 1000);
            return;
          }
          
          // Check if there's a stored return URL (e.g. from pricing page flow)
          const returnUrl = sessionStorage.getItem('oauth_return_url');
          
          // Clear session storage
          sessionStorage.removeItem('oauth_return_url');
          sessionStorage.removeItem('oauth_provider');
          sessionStorage.removeItem('selected_plan');
          
          // Redirect to studio subdomain after login
          setTimeout(() => {
            const hostname = window.location.hostname;
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
              navigate(returnUrl || '/projects');
            } else {
              const rootHostname = hostname.replace(/^www\./, '');
              const studioHostname = rootHostname.startsWith('studio') ? rootHostname : 'studio.' + rootHostname;
              const targetPath = returnUrl || '/projects';
              window.location.replace(`${window.location.protocol}//${studioHostname}${targetPath}`);
            }
          }, 2000);
        }
        return;
      } catch (error) {
        console.error('Failed to create user session:', error);
        setStatus({
          loading: false,
          success: false,
          error: 'Failed to create user session. Please try again.'
        });
        return;
      }
    }

    // Legacy fallback: Check for authorization code (old flow)
    const code = searchParams.get('code');
    const currentUser = AuthService.getCurrentUser();

    if (!code) {
      setStatus({
        loading: false,
        success: false,
        error: 'No authorization data received. Please try the connection process again.'
      });
      return;
    }

    // Check if user is authenticated for legacy flow
    if (!currentUser?.id) {
      setStatus({
        loading: false,
        success: false,
        error: 'User session not found. Please sign in again.'
      });
      return;
    }

    try {
      // Process the OAuth callback using provider-specific handler (legacy)
      const response = await providerConfig.handler(code);
      
      if (response.success) {
        setStatus({
          loading: false,
          success: true,
          error: null
        });
        
        if (isPopup) {
          // Send success result to parent window and close popup
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_SUCCESS',
              provider,
              user: response.user || {
                provider,
                email: `user@${provider === 'google' ? 'gmail.com' : 'outlook.com'}`,
                name: `${providerConfig.name} User`
              }
            }, '*');
            window.close();
          }
        } else {
          // Normal flow - redirect to calendar settings after a brief delay
          setTimeout(() => {
            navigate('/calendar/settings');
          }, 2000);
        }
      } else {
        throw new Error(response.message || 'Unknown error occurred');
      }
    } catch (err) {
      console.error('Callback processing error:', err);
      const errorMessage = `Failed to connect ${providerConfig.name}. Please try again.`;
      
      setStatus({
        loading: false,
        success: false,
        error: errorMessage
      });
      
      if (isPopup) {
        // Send error result to parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_ERROR',
            provider,
            error: errorMessage
          }, '*');
          // Don't close immediately on error, let user see the message
        }
      }
    }
  };

  const handleRetry = () => {
    navigate('/projects');
  };

  return (
    <>
      <Helmet>
        <title>Connecting... - Teamy</title>
        <meta name="description" content="Connecting your account to Teamy." />
      </Helmet>
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light callback-page-wrapper">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card">
              <div className={`card-body text-center ${status.loading && !status.error ? 'spinner-only' : 'p-5'}`}>
                {status.loading && !status.error && (
                  <div className="rotating-icon-container">
                    <ion-spinner name="crescent"></ion-spinner>
                  </div>
                )}
                {status.error && (
                  <>
                    <div className="mb-4" style={{ fontSize: '3rem' }}>❌</div>
                    <h4 className="text-danger mb-3">Connection Failed</h4>
                    <StatusMessage
                      type="error"
                      message={status.error}
                      className="text-start mb-4"
                    />
                    {!isPopup && (
                      <button
                        className="btn btn-primary"
                        onClick={handleRetry}
                      >
                        Return to Settings
                      </button>
                    )}
                    {isPopup && (
                      <button
                        className="btn btn-primary"
                        onClick={() => window.close()}
                      >
                        Close Window
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CallbackPage;
